import { BaseService, useServiceStore } from "@opendash/core";
import {
  changeLanguage,
  getCurrentLanguageSync,
  translate,
  translateSync,
} from "@opendash/i18n";

import Parse from "parse";

import {
  GlossaryEntry,
  Invoice,
  InvoicePage,
  Language,
  TenantConfig,
  TenantDomain,
  Video,
} from "./types";

import { DEFAULT_TENANT_ID } from "./const";

interface StateInterface {
  tenantId: string | null;
  tenantConfig: TenantConfig | undefined;
  invoices: Invoice[];
  invoicePages: InvoicePage[];
  glossaryEntries: GlossaryEntry[];
  glossaryEntriesMapById: Record<string, GlossaryEntry>;
  glossaryEntriesMapByKeyword: Record<string, GlossaryEntry>;
  gloassryEntriesMapByTag: Record<string, GlossaryEntry>;
  videos: Video[];
  frontpageImage: string | undefined;
  forceLanguage: string | undefined;
  embedded: boolean;
}

export class AppService extends BaseService<StateInterface> {
  error: boolean = false;
  errorTenantNotFound: boolean = false;

  constructor() {
    super({
      initialState: {
        tenantId: null,
        tenantConfig: undefined,
        invoices: [],
        invoicePages: [],
        glossaryEntries: [],
        glossaryEntriesMapById: {},
        glossaryEntriesMapByKeyword: {},
        gloassryEntriesMapByTag: {},
        videos: [],
        frontpageImage: undefined,
        forceLanguage: undefined,
        embedded: false,
      },
    });
  }

  public async init() {
    try {
      const tenantDomain = await new Parse.Query(TenantDomain)
        .equalTo("domain", window?.location?.hostname)
        .first();

      const tenant = tenantDomain?.get("tenant");
      const tenantId = tenant?.id;

      if (!tenant || !tenantId) {
        this.error = true;
        this.errorTenantNotFound = true;
        this.setLoading(false);

        return;
      }

      const tenantConfig = await new Parse.Query(TenantConfig)
        .equalTo("tenant", tenant)
        .first();

      if (
        tenantConfig?.get("forceHost") &&
        tenantConfig?.get("forceHost") !== window?.location?.hostname
      ) {
        window.location.href = `https://${tenantConfig?.get("forceHost")}${
          window?.location?.pathname
        }`;

        return;
      }

      const forceLanguage = tenantConfig?.get("forceLanguage")?.id;
      const currentLanguageId = forceLanguage || getCurrentLanguageSync();

      if (forceLanguage && forceLanguage !== getCurrentLanguageSync()) {
        changeLanguage(forceLanguage);
      }

      const defaultTenant = new Parse.Object("OD3_Tenant", {
        id: DEFAULT_TENANT_ID,
      });

      //console.log("current language", currentLanguageId);

      await translate("app:menu.frontpage");

      const frontpageImage = await preloadImage(
        "frontpage-image",
        tenantConfig?.get("frontpageBackgroundImage")?.url()
      );

      const currentLanguage = await new Parse.Query(Language).get(
        currentLanguageId
      );

      document.title = tenantConfig?.get("title") || "ASEW Rechnungserklärer";

      // Setze das Favicon basierend auf der Tenant-Konfiguration
      const faviconFile = tenantConfig?.get("favicon");
      const faviconUrl = faviconFile?.url();
      setFavicon(faviconUrl);

      const primaryColor = tenantConfig?.get("primaryColor") || "#ff0000";
      const secondaryColor = tenantConfig?.get("secondaryColor") || "#ff0000";
      const primaryTextColor =
        tenantConfig?.get("primaryTextColor") || "#ff0000";
      const secondaryTextColor =
        tenantConfig?.get("secondaryTextColor") || "#ff0000";

      let root = document.documentElement;

      // Ensure accessible contrast for text over primary/secondary backgrounds.
      const accessiblePrimaryText = pickAccessibleTextColor(
        primaryColor,
        primaryTextColor,
        4.5
      );
      const accessibleSecondaryText = pickAccessibleTextColor(
        secondaryColor,
        secondaryTextColor,
        4.5
      );

      root.style.setProperty("--primaryColor", primaryColor);
      root.style.setProperty("--primaryTextColor", accessiblePrimaryText);
      root.style.setProperty("--primaryColor-alpha", hexToRgbA(primaryColor));
      root.style.setProperty("--secondaryColor", secondaryColor);
      root.style.setProperty("--secondaryTextColor", accessibleSecondaryText);
      root.style.setProperty(
        "--secondaryColor-alpha",
        hexToRgbA(secondaryColor)
      );

      // Compute an accessible active menu background color based on primary color.
      // Ensure white text on the active background meets contrast; if not, darken the
      // primary color by mixing with black until contrast with white >= 4.5.
      function mixHexColors(hexA: string, hexB: string, weight: number) {
        const pa = parseHexColor(hexA);
        const pb = parseHexColor(hexB);
        if (!pa || !pb) return hexA;
        const r = Math.round(pa.r * (1 - weight) + pb.r * weight);
        const g = Math.round(pa.g * (1 - weight) + pb.g * weight);
        const b = Math.round(pa.b * (1 - weight) + pb.b * weight);
        const toHex = (v: number) =>
          v < 16 ? "0" + v.toString(16) : v.toString(16);
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }

      let activeMenuBg = primaryColor;
      const desiredRatio = 4.5;
      if (contrastRatio(activeMenuBg, "#ffffff") < desiredRatio) {
        // progressively darken by mixing up to 90% with black
        for (let i = 1; i <= 10; i++) {
          const weight = i * 0.08; // 0.08 .. 0.8
          const mixed = mixHexColors(primaryColor, "#000000", weight);
          if (contrastRatio(mixed, "#ffffff") >= desiredRatio) {
            activeMenuBg = mixed;
            break;
          }
          // if last iteration and still not enough contrast, keep the darkest
          if (i === 10) activeMenuBg = mixed;
        }
      }

      let activeMenuText = pickAccessibleTextColor(
        activeMenuBg,
        "#ffffff",
        desiredRatio
      );

      // Ensure the active menu text also contrasts with the header background
      // (AppLayout currently uses a white header background). If white text
      // would be invisible on the header, prefer the tenant primary color
      // if it has sufficient contrast; otherwise fall back to a guaranteed
      // readable color (black or the best contrast against header).
      const headerBg = "#ffffff";
      if (contrastRatio(activeMenuText, headerBg) < desiredRatio) {
        if (contrastRatio(primaryColor, headerBg) >= desiredRatio) {
          activeMenuText = primaryColor;
        } else {
          activeMenuText = pickAccessibleTextColor(
            headerBg,
            "#000000",
            desiredRatio
          );
        }
      }

      root.style.setProperty("--activeMenuBg", activeMenuBg);
      root.style.setProperty("--activeMenuTextColor", activeMenuText);

      const invoices = await new Parse.Query(Invoice)
        .equalTo("tenant", tenant)
        .descending("order")
        .find();

      const invoicePages = await new Parse.Query(InvoicePage)
        .equalTo("tenant", tenant)
        .descending("order")
        .find();

      const defaultGlossaryEntries = await new Parse.Query(GlossaryEntry)
        .equalTo("tenant", defaultTenant)
        .equalTo("language", currentLanguage)
        .ascending("title")
        .limit(999999999999)
        .find();

      const customGlossaryEntries = await new Parse.Query(GlossaryEntry)
        .equalTo("tenant", tenant)
        .equalTo("language", currentLanguage)
        .ascending("title")
        .limit(999999999999)
        .find();

      const allGlossaryEntries = [
        ...defaultGlossaryEntries,
        ...customGlossaryEntries,
      ];

      const glossaryEntriesMapByKeyword: Record<string, GlossaryEntry> = {};

      for (const e of allGlossaryEntries) {
        glossaryEntriesMapByKeyword[e.get("keyword")] = e;
      }

      const glossaryEntries = Object.values(glossaryEntriesMapByKeyword);

      glossaryEntries.sort(function (a, b) {
        const keyA = a.get("title").toUpperCase();
        const keyB = b.get("title").toUpperCase();
        // Compare the 2 dates

        if (keyA < keyB) return -1;
        if (keyA > keyB) return 1;
        return 0;
      });

      const glossaryEntriesMapById: Record<string, GlossaryEntry> = {};
      const gloassryEntriesMapByTag: Record<string, GlossaryEntry> = {};

      for (const entry of glossaryEntries) {
        glossaryEntriesMapById[entry.id] = entry;

        gloassryEntriesMapByTag[entry.get("title")] = entry;

        for (const tag of entry.get("tags") as string[]) {
          gloassryEntriesMapByTag[tag] = entry;
        }
      }

      //console.log("TEST", tenantConfig?.get("hideVideoIds"));
      let videos = await new Parse.Query(Video)
        .equalTo("tenant", tenant)
        // .equalTo("language", currentLanguage)
        .descending("order")
        .find();

      if (tenantId !== DEFAULT_TENANT_ID) {
        const defaultVideos = await new Parse.Query(Video)
          .equalTo("tenant", defaultTenant)
          // .equalTo("language", currentLanguage)
          .descending("order")
          .find();

        videos = [...videos, ...defaultVideos].sort(
          (a, b) => b.get("order") - a.get("order")
        );
      }
      videos = videos.filter(
        (video) => !tenantConfig?.hideVideoIds.includes(video.id)
      );

      /*
      if (tenantId === "JgpCYeVWXc") {
        videos = videos.filter((v) => v.id !== "KTQB3RolhA");
        videos = videos.filter((v) => v.id !== "ZuEm0R5DQP");
        videos = videos.filter((v) => v.id !== "GeGrDaCY97");
      }

      if (tenantId === "aGZR6AgRdt") {
        videos = videos.filter((v) => v.id !== "PDRzhtk8FB");
      }

      if (tenantId === "gywDzRgrKk") {
        videos = videos.filter((v) => v.id !== "J5oc9s1BA9");
      }

      if (tenantId === "aoBQ6s98Kl") {
        videos = videos.filter((v) => v.id !== "J5oc9s1BA9");
        videos = videos.filter((v) => v.id !== "PmMkUiNmtL");
        videos = videos.filter((v) => v.id !== "vMBmhUZYk1");
        videos = videos.filter((v) => v.id !== "lZggfAg8OC");
        videos = videos.filter((v) => v.id !== "ZuEm0R5DQP");
        videos = videos.filter((v) => v.id !== "7iCB2AEMgH");
        videos = videos.filter((v) => v.id !== "IBGFIfCkCP");
      }
*/
      this.store.update((draft) => {
        draft.tenantId = tenantId;
        draft.tenantConfig = tenantConfig;
        draft.invoices = invoices;
        draft.invoicePages = invoicePages;
        draft.glossaryEntries = glossaryEntries;
        draft.glossaryEntriesMapById = glossaryEntriesMapById;
        draft.glossaryEntriesMapByKeyword = glossaryEntriesMapByKeyword;
        draft.gloassryEntriesMapByTag = gloassryEntriesMapByTag;
        draft.videos = videos;
        draft.frontpageImage = frontpageImage;
        draft.forceLanguage = forceLanguage;
      });

      this.setLoading(false);

      console.log("init Rechnungserklärer");
      //console.log("tenant.id =", tenantId);
      //console.log("tenantConfig.id =", tenantConfig!.id);
      //console.log("parentTenant.id =", DEFAULT_TENANT_ID);
    } catch (error) {
      console.error(error);

      this.error = true;
      this.setLoading(false);
    } finally {
      window.localStorage.removeItem("Parse/asew/installationId");
    }
  }
}

export const app = new AppService();

const defaultSelector = (state: StateInterface) => state;

export function useAppState(): StateInterface;
export function useAppState<T = any>(selector: (state: StateInterface) => T): T;
export function useAppState<T = any>(selector?: (state: StateInterface) => T) {
  if (selector) {
    return useServiceStore(app, selector);
  } else {
    return useServiceStore(app, defaultSelector) as any as StateInterface;
  }
}

export function getTitle(title: string | undefined) {
  if (getCurrentLanguageSync() !== "de") {
    switch (title) {
      case "Strom":
      case "Stromrechnung":
      case "Ökostrom":
      case "Ökostromrechnung":
      case "Strom-Rechnung":
      case "Rechnung Strom":
        return translateSync("app:invoice.electricity");
      case "Gas":
      case "Erdgas":
      case "Gasrechnung":
      case "Erdgasrechnung":
      case "Ökogas":
      case "Ökogasrechnung":
      case "Gas-Rechnung":
      case "Rechnung Gas":
      case "Rechnung Erdgas":
        return translateSync("app:invoice.gas");
      case "Wasser":
      case "Wasserrechnung":
      case "Trinkwasser":
      case "Wasser-Rechnung":
      case "Rechnung Wasser":
      case "Rechnung Trinkwasser":
        return translateSync("app:invoice.water");
      case "Fernwärme":
      case "Fernwärmerechnung":
      case "Wärme":
      case "Wärmerechnung":
      case "Wärmelieferung":
      case "Rechnung Fernwärme":
        return translateSync("app:invoice.remoteheating");
      case "Fernwärme (Kamen)":
        return translateSync("app:invoice.remoteheatingkamen");
      case "Fernwärme (Bergkamen)":
        return translateSync("app:invoice.remoteheatingbergkamen");
      case "Fernwärme (Kamen, Seseke Aue)":
        return translateSync("app:invoice.remoteheating_kamen_sesekeaue");
      case "Gesamt":
      case "Gesamtrechnung":
      case "Energie":
      case "Energierechnung":
      case "Jahresendabrechnung":
      case "Jahresrechnung":
      case "Strom, Gas, Wasser":
      case "Strom, Gas & Wasser":
      case "Energie- und Wasserrechnung":
      case "Jahresabrechnung":
      case "Abrechnung":
      case "Rechnung Strom, Gas, Wasser":
      case "Rechnung Strom/Gas/Wasser":
      case "Strom/Gas/Wasser":
      case "Strom / Gas / Wasser":
        return translateSync("app:invoice.energy");
      case "Übersicht":
        return translateSync("app:invoice.overview");
      case "Strom- und Gaspreisbremse":
        return translateSync("app:invoice.pricecap");
      case "Strom (Eintarif)":
      case "Strom (ET)":
      case "Stromrechnung (Eintarif)":
      case "Stromrechnung (ET)":
        return translateSync("app:invoice.electricitysingle");
      case "Strom (Zweitarif)":
      case "Strom (ZT)":
      case "Strom (HT/NT)":
      case "Strom (HT / NT)":
      case "Stromrechnung (Zweitarif)":
      case "Stromrechnung (ZT)":
      case "Stromrechnung (HT/NT)":
      case "Stromrechnung (HT / NT)":
        return translateSync("app:invoice.electricitydual");
      case "Abwasser":
      case "Abwasserrechnung":
      case "Rechnung Abwasser":
      case "Abwasser-Rechnung":
        return translateSync("app:invoice.wastewater");
      case "Wasser-/ Abwasserrechnung":
      case "Wasser / Abwasserrechnung":
      case "Wasser/Abwasser":
      case "Rechnung Wasser & Abwasser":
      case "Rechnung Wasser/Abwasser":
      case "Wasser, Abwasser":
        return translateSync("app:invoice.water_and_wastewater");
      case "Breitband-Rechnung":
      case "Breitband":
      case "Rechnung Breitband":
      case "Breitbandrechnung":
      case "Internetrechnung":
      case "Rechnung Internet":
      case "Internet-Rechnung":
      case "Internet":
        return translateSync("app:invoice.broadband");
      case "Dynamischer Tarif":
      case "Dynamische Tarife":
      case "Dynamischer Stromtarif":
      case "Strom dynamisch":
      case "Rechnung dynamischer Strom":
      case "Strom (dynamisch)":
        return translateSync("app:invoice.electricity_dynamic");
      case "Wärme-Contracting":
      case "Wärmecontracting":
      case "Contracting":
      case "Heizungscontracting":
      case "Heizungs-Contracting":
      case "Contracting (Wärmelieferung)":
      case "Rechnung Contracting":
      case "Contractingrechnung":
        return translateSync("app:invoice.heating_contracting");
    }
  }

  return title || "";
}

export function getInvoiceTitle(invoice: Invoice) {
  const title = invoice.get("title");

  return getTitle(title);
}

function hexToRgbA(hex: string) {
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    let c: string[] = hex.substring(1).split("");

    if (c.length == 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }

    const r = parseInt(c[0] + c[1], 16);
    const g = parseInt(c[2] + c[3], 16);
    const b = parseInt(c[4] + c[5], 16);

    return "rgba(" + [r, g, b].join(",") + ",0.75)";
  }

  return hex;
}

function parseHexColor(
  hex: string
): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  const m = hex.trim().toLowerCase();
  if (!m.startsWith("#")) return null;
  const h = m.substring(1);
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return { r, g, b };
  }
  if (h.length === 6) {
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return { r, g, b };
  }
  return null;
}

function relativeLuminance({ r, g, b }: { r: number; g: number; b: number }) {
  // convert sRGB component (0..255) to linear value
  const srgb = [r, g, b].map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function contrastRatio(hexA: string, hexB: string) {
  const a = parseHexColor(hexA);
  const b = parseHexColor(hexB);
  if (!a || !b) return 1;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const L1 = Math.max(la, lb);
  const L2 = Math.min(la, lb);
  return (L1 + 0.05) / (L2 + 0.05);
}

function pickAccessibleTextColor(
  bgHex: string,
  preferredHex: string,
  minRatio = 4.5
) {
  try {
    const ratio = contrastRatio(bgHex, preferredHex);
    if (ratio >= minRatio) return preferredHex;

    const blackRatio = contrastRatio(bgHex, "#000000");
    const whiteRatio = contrastRatio(bgHex, "#ffffff");

    return blackRatio > whiteRatio ? "#000000" : "#ffffff";
  } catch (e) {
    return preferredHex;
  }
}

async function preloadImage(key: string, src: string | undefined) {
  if (!src) {
    return undefined;
  }

  const response = await fetch(src);

  const blob = await response.blob();

  return URL.createObjectURL(blob);
}

function setFavicon(faviconUrl: string | undefined) {
  if (!faviconUrl) {
    return;
  }

  // Bestimme das Format basierend auf der URL
  const isPng = faviconUrl.toLowerCase().includes(".png");
  const isSvg = faviconUrl.toLowerCase().includes(".svg");
  const isIco = faviconUrl.toLowerCase().includes(".ico");

  let mimeType = "image/x-icon"; // Standard fallback

  if (isPng) {
    mimeType = "image/png";
  } else if (isSvg) {
    mimeType = "image/svg+xml";
  } else if (isIco) {
    mimeType = "image/x-icon";
  }

  // Entferne alle existierenden Favicon-Links
  const existingLinks = document.querySelectorAll('link[rel*="icon"]');
  existingLinks.forEach((link) => link.remove());

  // Safari-spezifischer Fix: Warte kurz bevor neue Links hinzugefügt werden
  setTimeout(() => {
    // Erstelle neue Favicon-Links mit Safari-optimierten Einstellungen

    // Standard Favicon für alle Browser
    const linkStandard = document.createElement("link");
    linkStandard.rel = "icon";
    linkStandard.type = mimeType;
    linkStandard.href = faviconUrl;
    document.head.appendChild(linkStandard);

    // 16x16 Favicon
    const link16 = document.createElement("link");
    link16.rel = "icon";
    link16.type = mimeType;
    link16.setAttribute("sizes", "16x16");
    link16.href = faviconUrl;
    document.head.appendChild(link16);

    // 32x32 Favicon
    const link32 = document.createElement("link");
    link32.rel = "icon";
    link32.type = mimeType;
    link32.setAttribute("sizes", "32x32");
    link32.href = faviconUrl;
    document.head.appendChild(link32);

    // Safari-spezifischer "shortcut icon" (Legacy-Support)
    const linkShortcut = document.createElement("link");
    linkShortcut.rel = "shortcut icon";
    linkShortcut.type = mimeType;
    linkShortcut.href = faviconUrl;
    document.head.appendChild(linkShortcut);

    // Apple Touch Icon (für Safari auf iOS/macOS)
    const linkApple = document.createElement("link");
    linkApple.rel = "apple-touch-icon";
    linkApple.setAttribute("sizes", "180x180");
    linkApple.href = faviconUrl;
    document.head.appendChild(linkApple);

    // Safari-spezifischer Touch Icon ohne "precomposed"
    const linkTouch = document.createElement("link");
    linkTouch.rel = "apple-touch-icon-precomposed";
    linkTouch.setAttribute("sizes", "180x180");
    linkTouch.href = faviconUrl;
    document.head.appendChild(linkTouch);

    // Safari-spezifischer Trick: Erzwinge Browser-Update durch kurze URL-Änderung
    if (
      navigator.userAgent.includes("Safari") &&
      !navigator.userAgent.includes("Chrome")
    ) {
      // Safari macOS: Mehrfache Cache-Busting Strategien
      const isMacOS =
        navigator.userAgent.includes("Macintosh") ||
        navigator.userAgent.includes("Mac OS");

      if (isMacOS) {
        // Strategie 1: Sofortiges Cache-Busting
        const timestamp = Date.now();
        const cacheBustingUrl1 =
          faviconUrl +
          (faviconUrl.includes("?") ? "&" : "?") +
          "cb=" +
          timestamp;

        const safariLink1 = document.createElement("link");
        safariLink1.rel = "icon";
        safariLink1.type = mimeType;
        safariLink1.href = cacheBustingUrl1;
        document.head.appendChild(safariLink1);

        // Strategie 2: Favicon mit anderem rel-Attribut
        setTimeout(() => {
          const cacheBustingUrl2 =
            faviconUrl +
            (faviconUrl.includes("?") ? "&" : "?") +
            "safari2=" +
            (timestamp + 100);

          const safariLink2 = document.createElement("link");
          safariLink2.rel = "shortcut icon";
          safariLink2.type = mimeType;
          safariLink2.href = cacheBustingUrl2;
          document.head.appendChild(safariLink2);
        }, 100);

        // Strategie 3: Force DOM Manipulation
        setTimeout(() => {
          const cacheBustingUrl3 =
            faviconUrl +
            (faviconUrl.includes("?") ? "&" : "?") +
            "force=" +
            (timestamp + 200);

          // Entferne alle und füge neu hinzu
          const allIcons = document.querySelectorAll('link[rel*="icon"]');
          allIcons.forEach((link) => link.remove());

          // Füge das Haupt-Favicon wieder hinzu
          const mainIcon = document.createElement("link");
          mainIcon.rel = "icon";
          mainIcon.type = mimeType;
          mainIcon.href = cacheBustingUrl3;
          document.head.appendChild(mainIcon);
        }, 300);

        // Strategie 4: Tab-Titel kurz ändern (forciert Safari-Refresh)
        setTimeout(() => {
          const originalTitle = document.title;
          document.title = originalTitle + " ";

          setTimeout(() => {
            document.title = originalTitle;
          }, 50);
        }, 500);

        // Strategie 5: Letzter Versuch mit verzögertem Standard-Favicon
        setTimeout(() => {
          const finalIcon = document.createElement("link");
          finalIcon.rel = "icon";
          finalIcon.type = mimeType;
          finalIcon.href =
            faviconUrl +
            (faviconUrl.includes("?") ? "&" : "?") +
            "final=" +
            Date.now();
          document.head.appendChild(finalIcon);
        }, 1000);
      } else {
        // Standard Safari (iOS) Behandlung
        setTimeout(() => {
          const cacheBustingUrl =
            faviconUrl +
            (faviconUrl.includes("?") ? "&" : "?") +
            "safari=" +
            Date.now();

          const safariLink = document.createElement("link");
          safariLink.rel = "icon";
          safariLink.type = mimeType;
          safariLink.href = cacheBustingUrl;
          document.head.appendChild(safariLink);

          setTimeout(() => {
            if (safariLink.parentNode) {
              safariLink.parentNode.removeChild(safariLink);
            }
          }, 500);
        }, 200);
      }
    }
  }, 50);
}
